import React from 'react';
import ReactTags from 'react-tag-autocomplete';
import PropTypes from 'prop-types';

import { getTagNames } from '@api/v4/brandfolders/tags';
import sanitize from '@helpers/sanitize';

class ScopeTagSelection extends React.Component {
  state = {
    animateNewTag: false,
    duplicateTag: false,
    inputValue: '',
    suggestedTags: [],
    tagsArray: []
  }

  componentDidMount() {
    this.fetchExistingTags();
    if (this.props.scope?.tag_names) {
      this.setState({ tagsArray: this.props.scope.tag_names.map((tag) => ({ name: sanitize(tag) })) });
    }
  }


  customFilterSuggestions = (textInputValue, possibleSuggestionsArray) => {
    // suggestion tag filters/sort: (baseline is all tags tagged in resource)
    // 1. remove if tag does not include query
    // 2. sort by tag count (higher count display first)
    // 3. if same count, sort by index (tags with query earliest in tag display first)

    // using state for query value because default filter function (textInputValue)
    // applies filter for each letter combination of query, e.g. if
    // textInputValue == 'alt', this filter function runs three times, once for
    // 'alt' then for 'al' then for 'a'
    const query = this.state.inputValue.toLowerCase();
    const suggestions = [];

    possibleSuggestionsArray.forEach((tag) => {
      const tagText = tag.text.toLowerCase();

      if (query && tagText.indexOf(query) > -1) {
        tag.queryIndex = tagText.indexOf(query); // eslint-disable-line no-param-reassign
        suggestions.push(tag);
      }
    });

    suggestions.sort((a, b) => (
      b.count - a.count || a.queryIndex - b.queryIndex
    ));

    return suggestions;
  }

  fetchExistingTags() {
    getTagNames().then((response) => {
      const formattedNames = response.map((name) => ({ id: name, name }));
      this.setState({ suggestedTags: formattedNames });
    });
  }

  sortTags(a, b) {
    if (a.toUpperCase() > b.toUpperCase()) {
      return 1;
    }

    if (b.toUpperCase() > a.toUpperCase()) {
      return -1;
    }

    return 0;
  }

  handleInputChange(text) {
    this.setState({ inputValue: text, duplicateTag: this.detectDuplicateTag(text) });
  }

  detectDuplicateTag(tag) {
    const tagsTextArray = [...this.state.tagsArray].map((t) => t.name.toUpperCase());
    const tagText = typeof tag.name === 'undefined' ? tag : tag.name;

    return tagsTextArray.indexOf(tagText.toUpperCase()) > -1;
  }

  deleteTags(i) {
    this.setState((state) => {
      const tags = [...state.tagsArray];
      tags.splice(i, 1);

      this.props.updateScope({ ...this.props.scope, tag_names: tags.map((taggy) => taggy.name) });

      return {
        tagsArray: tags,
        animateNewTag: false,
      };
    });
  }

  addNewTags(tag) {
    if (this.state.duplicateTag || sanitize(tag.name) === '') {
      this.setState({ inputValue: '', duplicateTag: false });
      return;
    }

    const suggestedTagsUpdated = [];

    this.state.suggestedTags.forEach((suggestedTag) => {
      if (suggestedTag.name !== tag.name) {
        suggestedTagsUpdated.push(suggestedTag);
      }
    });

    const updatedTags = [...this.state.tagsArray, { name: sanitize(tag.name) }];

    this.setState(() => ({
      tagsArray: updatedTags,
      suggestedTags: suggestedTagsUpdated,
      animateNewTag: true,
      inputValue: '',
      duplicateTag: false
    }), () => {
      this.props.updateScope({ ...this.props.scope, tag_names: updatedTags.map((taggy) => taggy.name) });
      setTimeout(() => {
        this.setState({ animateNewTag: false });
      }, 800);
    });
  }

  tagInfoClass() {
    return this.state.inputValue === '' || this.state.duplicateTag ? '' : 'active';
  }

  renderClass(newClass) {
    const stateVar = newClass.replace(/-([a-z])/g, (g) => g[1].toUpperCase()); // convert dash-case to camelCase

    return this.state[stateVar] ? newClass : '';
  }

  render() {
    const { suggestedTags, tagsArray } = this.state;

    const tagComponent = (props) => (
      <div className={props.classNames.selectedTag}>
        <span className={props.classNames.selectedTagName}>{props.tag.name}</span>
        <button
          className="react-tags__remove"
          onClick={props.onDelete}
          type="button"
        >
          <span className="bff-close" />
        </button>
      </div>
    );

    return (
      <div className="modal-row">
        <div className={`tag-info-container ${this.tagInfoClass()}`}>
          <span className="tag-info">{bfTranslate('Hit "Enter" to create new tag')}</span>
        </div>
        <div className={`duplicate-warning-container ${this.renderClass('duplicate-tag')}`}>
          <span className="bff-warning" />
          <h5 className="duplicate-warning">{bfTranslate('Tag already exists')}</h5>
        </div>
        <ReactTags
          allowBackspace={false}
          allowDeleteFromEmptyInput={false}
          allowNew
          autofocus={false}
          classNames={{ tags: 'tags-container', searchInput: 'inputs' }}
          delimiters={[9, 13, 188]}
          handleAddition={(tag) => this.addNewTags(tag)}
          handleDelete={(i) => this.deleteTags(i)}
          handleFilterSuggestions={this.customFilterSuggestions}
          handleInputChange={(text) => this.handleInputChange(text)}
          minQueryLength={1}
          placeholder={bfTranslate("Type to add new tag")}
          suggestions={suggestedTags}
          tagComponent={tagComponent}
          tags={tagsArray}
        />
      </div>
    );
  }
}

ScopeTagSelection.propTypes = {
  updateScope: PropTypes.func.isRequired,
  scope: PropTypes.shape({})
};

ScopeTagSelection.defaultProps = {
  scope: false
};

export default ScopeTagSelection;
